import React, { memo, useMemo } from 'react';
import {
  PortvilleBillingSettings
} from '../../../../types/portvilleBilling';
import { PortvilleRoomWithResidency } from '../../../../types/portvilleRoom';

interface RoomWithSettings extends PortvilleRoomWithResidency {
  billingSettings?: PortvilleBillingSettings;
}

interface BillingSettingsTableProps {
  rooms: RoomWithSettings[];
  onEdit: (settings: PortvilleBillingSettings) => void;
  onShowTimeline?: (roomId: string) => void;
  loading: boolean;
}

const BillingSettingsTable: React.FC<BillingSettingsTableProps> = memo(({
  rooms,
  onEdit,
  onShowTimeline,
  loading
}) => {
  // 포매터 최적화 (useMemo로 캐싱)
  const formatters = useMemo(() => ({
    currency: new Intl.NumberFormat('ko-KR'),
    date: new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  }), []);

  const formatCurrency = (amount: number): string => formatters.currency.format(amount);
  const formatDate = (date: Date): string => formatters.date.format(date);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="text-gray-600">데이터를 불러오는 중...</span>
        </div>
      </div>
    );
  }

  if (rooms.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0v-5a2 2 0 012-2h6a2 2 0 012 2v5M7 7h10M7 10h10M7 13h7" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">호실이 없습니다</h3>
        <p className="mt-1 text-sm text-gray-500">
          먼저 포트빌 호실을 등록해 주세요.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              호실정보
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              입주자/담당자
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              관리비 설정 / 버전
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              월 기본료
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              계산방식
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              적용기간
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              작업
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {rooms.map((room) => (
            <BillingSettingsTableRow
              key={room.id}
              room={room}
              onEdit={onEdit}
              onShowTimeline={onShowTimeline}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
});

interface BillingSettingsTableRowProps {
  room: RoomWithSettings;
  onEdit: (settings: PortvilleBillingSettings) => void;
  onShowTimeline?: (roomId: string) => void;
  formatCurrency: (amount: number) => string;
  formatDate: (date: Date) => string;
}

const BillingSettingsTableRow = memo<BillingSettingsTableRowProps>(({
  room,
  onEdit,
  onShowTimeline,
  formatCurrency,
  formatDate
}) => (
  <tr className="hover:bg-gray-50">
    {/* 호실 정보 */}
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="flex items-center">
        <div className="text-sm font-medium text-gray-900">
          {room.roomNumber}호
        </div>
        <div className="ml-2">
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            room.status === 'occupied' ? 'bg-red-100 text-red-800' :
            room.status === 'preparing' ? 'bg-yellow-100 text-yellow-800' :
            room.status === 'vacant' ? 'bg-green-100 text-green-800' :
            room.status === 'facility' ? 'bg-purple-100 text-purple-800' :
            'bg-blue-100 text-blue-800'
          }`}>
            {room.status === 'occupied' ? '입주중' :
             room.status === 'preparing' ? '준비중' :
             room.status === 'vacant' ? '공실' :
             room.status === 'facility' ? '시설물' : '계약진행'}
          </span>
        </div>
      </div>
    </td>

    {/* 입주자/담당자 정보 */}
    <td className="px-6 py-4 whitespace-nowrap">
      {room.roomType === '시설물' && room.facilityInfo ? (
        <div>
          <div className="text-sm font-medium text-gray-900">
            {room.facilityInfo.facilityName}
          </div>
          <div className="text-sm text-gray-500">
            담당자: {room.facilityInfo.responsiblePerson.name}
          </div>
          <div className="text-sm text-gray-500">
            {room.facilityInfo.responsiblePerson.organization}
          </div>
          <div className="text-sm text-gray-400">
            {room.facilityInfo.responsiblePerson.phoneNumber}
          </div>
        </div>
      ) : room.currentResident ? (
        <div>
          <div className="text-sm font-medium text-gray-900">
            {room.currentResident.name}
          </div>
          <div className="text-sm text-gray-500">
            {room.currentResident.organization}
          </div>
          <div className="text-sm text-gray-400">
            {room.currentResident.phoneNumber}
          </div>
        </div>
      ) : (
        <div className="text-sm text-gray-400">
          {room.roomType === '시설물' ? '시설물 정보 없음' : '입주자 없음'}
        </div>
      )}
    </td>

    {/* 관리비 설정 상태 및 버전 */}
    <td className="px-6 py-4 whitespace-nowrap">
      {room.billingSettings ? (
        <div>
          <div className="flex items-center mb-1">
            <div className="flex-shrink-0 w-2.5 h-2.5 bg-green-400 rounded-full"></div>
            <div className="ml-2 text-sm text-gray-900">설정 완료</div>
          </div>
          <div className="flex items-center space-x-2 text-xs text-gray-500">
            <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">
              v{room.billingSettings.version || 1}
            </span>
            {room.billingSettings.isActive && (
              <span className="bg-green-100 text-green-700 px-2 py-1 rounded">
                활성
              </span>
            )}
          </div>
          {room.billingSettings.changeReason && (
            <div className="mt-1 text-xs text-gray-400 truncate max-w-32" title={room.billingSettings.changeReason}>
              {room.billingSettings.changeReason}
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center">
          <div className="flex-shrink-0 w-2.5 h-2.5 bg-red-400 rounded-full"></div>
          <div className="ml-2 text-sm text-gray-900">설정 필요</div>
        </div>
      )}
    </td>

    {/* 월 기본료 */}
    <td className="px-6 py-4 whitespace-nowrap">
      {room.billingSettings ? (
        <div className="text-sm text-gray-900">
          <div>임대료: {formatCurrency(room.billingSettings.baseRates.monthlyRent)}원</div>
          <div>관리비: {formatCurrency(room.billingSettings.baseRates.managementFee)}원</div>
          <div className="text-xs text-gray-500">
            통신료: {formatCurrency(room.billingSettings.baseRates.communicationFee)}원
          </div>
        </div>
      ) : (
        <div className="text-sm text-gray-400">-</div>
      )}
    </td>

    {/* 호실 설정 */}
    <td className="px-6 py-4 whitespace-nowrap">
      {room.billingSettings && room.billingSettings.roomSettings ? (
        <div className="text-sm text-gray-900">
          <div>전기비율: {((room.billingSettings.roomSettings.electricityAreaContributionRatio || (room.billingSettings.roomSettings as any).areaContributionRatio || 0) * 100).toFixed(4)}%</div>
          <div>수도비율: {((room.billingSettings.roomSettings.waterAreaContributionRatio || (room.billingSettings.roomSettings as any).areaContributionRatio || 0) * 100).toFixed(4)}%</div>
          <div>타입: {room.billingSettings.roomSettings.roomType}</div>
          {room.billingSettings.billingConfig.autoGenerate && (
            <div className="text-xs text-blue-600">자동생성</div>
          )}
        </div>
      ) : (
        <div className="text-sm text-gray-400">-</div>
      )}
    </td>

    {/* 적용 기간 */}
    <td className="px-6 py-4 whitespace-nowrap">
      {room.billingSettings ? (
        <div className="text-sm text-gray-900">
          <div>{formatDate(room.billingSettings.effectiveFrom)} ~</div>
          {room.billingSettings.effectiveTo ? (
            <div>{formatDate(room.billingSettings.effectiveTo)}</div>
          ) : (
            <div className="text-gray-500">무기한</div>
          )}
        </div>
      ) : (
        <div className="text-sm text-gray-400">-</div>
      )}
    </td>

    {/* 작업 버튼 */}
    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
      <div className="flex items-center justify-end space-x-2">
        {room.billingSettings ? (
          <>
            <button
              onClick={() => onEdit(room.billingSettings!)}
              className="text-blue-600 hover:text-blue-900 transition-colors"
            >
              수정
            </button>
            {onShowTimeline && (
              <button
                onClick={() => onShowTimeline(room.id)}
                className="text-indigo-600 hover:text-indigo-900 transition-colors"
                title="설정 타임라인 보기"
              >
                히스토리
              </button>
            )}
          </>
        ) : (
          <span className="text-gray-400">설정 필요</span>
        )}
      </div>
    </td>
  </tr>
));

BillingSettingsTableRow.displayName = 'BillingSettingsTableRow';

export default BillingSettingsTable;
